import { useState, useEffect } from 'react';
import { 
  ChatBubbleLeftRightIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import Header from '../components/Header';
import toast from 'react-hot-toast';
import { config } from '../config/environment';

interface FeedbackItem {
  id: string;
  feedback_type: string;
  status: string;
  title: string;
  description: string;
  user_name?: string;
  user_email?: string;
  product_id?: string;
  component_id?: string;
  field_name?: string;
  current_value?: string;
  suggested_value?: string;
  source_url?: string;
  source_description?: string;
  confidence_level?: number;
  priority_display: string;
  has_source: boolean;
  is_correction: boolean;
  admin_response?: string;
  created_at: string;
  updated_at: string;
}

interface FeedbackStats {
  total_feedback: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  recent_high_priority: Array<{
    id: string;
    title: string;
    feedback_type: string;
    priority: number;
    created_at: string;
  }>;
}

const AdminFeedback = () => {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const loadFeedback = async () => {
    try {
      setIsLoading(true);
      const API_BASE = `${config.apiBaseUrl}/api`;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedType !== 'all') params.append('feedback_type', selectedType);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      
      const [feedbackResponse, statsResponse] = await Promise.all([
        fetch(`${API_BASE}/feedback/?${params.toString()}`),
        fetch(`${API_BASE}/feedback/stats/summary`)
      ]);

      if (!feedbackResponse.ok || !statsResponse.ok) {
        throw new Error('Failed to load feedback data');
      }

      const feedbackData = await feedbackResponse.json();
      const statsData = await statsResponse.json();
      
      setFeedback(feedbackData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load feedback:', error);
      toast.error('Failed to load feedback data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [selectedType, selectedStatus]);

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'correction':
        return <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />;
      case 'bug_report':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'feature_request':
        return <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'implemented':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'under_review':
        return <EyeIcon className="w-5 h-5 text-blue-500" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'implemented':
        return 'bg-green-100 text-green-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-purple-100 text-purple-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'medium-high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
      case 'medium-low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <>
        <title>Admin Feedback - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading feedback data...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>Admin Feedback - ProductTrace</title>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Feedback Management</h1>
            <p className="text-gray-600 mt-2">Review and manage user feedback and corrections</p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Feedback</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_feedback}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-8 h-8 text-orange-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Corrections</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.by_type.correction || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <ClockIcon className="w-8 h-8 text-yellow-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.by_status.pending || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-8 h-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Implemented</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.by_status.implemented || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Type
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="correction">Corrections</option>
                  <option value="general_feedback">General Feedback</option>
                  <option value="data_improvement">Data Improvement</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="bug_report">Bug Report</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="implemented">Implemented</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={loadFeedback}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Feedback List */}
          <div className="space-y-4">
            {feedback.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback found</h3>
                <p className="text-gray-600">No feedback matches your current filters.</p>
              </div>
            ) : (
              feedback.map((item) => (
                <div key={item.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {getTypeIcon(item.feedback_type)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-medium text-gray-900 truncate">
                              {item.title}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                              {item.status.replace('_', ' ')}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority_display)}`}>
                              {item.priority_display} Priority
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <span className="capitalize">{item.feedback_type.replace('_', ' ')}</span>
                            {item.user_name && <span>by {item.user_name}</span>}
                            <span>{formatDate(item.created_at)}</span>
                            {item.has_source && (
                              <span className="inline-flex items-center text-green-600">
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                Has Source
                              </span>
                            )}
                          </div>

                          <p className="text-gray-700 mb-4">{item.description}</p>

                          {/* Correction Details */}
                          {item.is_correction && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                              <h4 className="font-medium text-orange-900 mb-2">Correction Details</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="font-medium text-orange-800">Field:</span>
                                  <span className="ml-2 text-orange-700">{item.field_name}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-orange-800">Confidence:</span>
                                  <span className="ml-2 text-orange-700">{item.confidence_level}/5</span>
                                </div>
                                <div>
                                  <span className="font-medium text-orange-800">Current Value:</span>
                                  <span className="ml-2 text-orange-700">{item.current_value}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-orange-800">Suggested Value:</span>
                                  <span className="ml-2 text-orange-700">{item.suggested_value}</span>
                                </div>
                              </div>
                              
                              {item.source_url && (
                                <div className="mt-3">
                                  <span className="font-medium text-orange-800">Source:</span>
                                  <a 
                                    href={item.source_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="ml-2 text-blue-600 hover:text-blue-700 underline"
                                  >
                                    {item.source_url}
                                  </a>
                                </div>
                              )}
                              
                              {item.source_description && (
                                <div className="mt-2">
                                  <span className="font-medium text-orange-800">Source Description:</span>
                                  <span className="ml-2 text-orange-700">{item.source_description}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Contact Info */}
                          {(item.user_email || item.user_name) && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                              <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                              <div className="text-sm text-gray-600">
                                {item.user_name && <div>Name: {item.user_name}</div>}
                                {item.user_email && <div>Email: {item.user_email}</div>}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {getStatusIcon(item.status)}
                        <button
                          onClick={() => toggleExpanded(item.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {expandedItems.has(item.id) ? (
                            <ChevronUpIcon className="w-5 h-5" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedItems.has(item.id) && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-3">Technical Details</h4>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-600">ID:</span>
                                <span className="ml-2 text-gray-900 font-mono">{item.id}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-600">Created:</span>
                                <span className="ml-2 text-gray-900">{formatDate(item.created_at)}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-600">Updated:</span>
                                <span className="ml-2 text-gray-900">{formatDate(item.updated_at)}</span>
                              </div>
                              {item.product_id && (
                                <div>
                                  <span className="font-medium text-gray-600">Product ID:</span>
                                  <span className="ml-2 text-gray-900 font-mono">{item.product_id}</span>
                                </div>
                              )}
                              {item.component_id && (
                                <div>
                                  <span className="font-medium text-gray-600">Component ID:</span>
                                  <span className="ml-2 text-gray-900 font-mono">{item.component_id}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium text-gray-900 mb-3">Admin Response</h4>
                            {item.admin_response ? (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-blue-900 text-sm">{item.admin_response}</p>
                              </div>
                            ) : (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-gray-600 text-sm italic">No admin response yet</p>
                                <button className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                                  Add Response
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Recent High Priority */}
          {stats && stats.recent_high_priority.length > 0 && (
            <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent High Priority Items</h2>
              <div className="space-y-3">
                {stats.recent_high_priority.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div>
                      <h3 className="font-medium text-red-900">{item.title}</h3>
                      <p className="text-sm text-red-700">
                        {item.feedback_type.replace('_', ' ')} • Priority {item.priority} • {formatDate(item.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const element = document.getElementById(item.id);
                        element?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-red-600 hover:text-red-700 font-medium text-sm"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminFeedback; 